import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import Core                   from '@/core/Core';
import OrdersDAO              from '@/domains/Orders/OrdersDAO';
import ContextMenuItem        from "@/lib/components/VContextMenu/ContextMenuItem";
import PreorderPosition       from "@/domains/Preorders/PreorderPosition";
import OrdersService          from "@/domains/Orders/OrdersService";
import Order                  from "@/domains/Orders/Order";
import PartnersService        from "@/domains/Partners/PartnersService";

/**
 * Сервис для работы с позициями предзаказов.
 */
class PreordersService {

  // ======= Загрузка =======

  /**
   * Загрузить список позиций предзаказов с пагинацией и сортировкой.
   * Принудительно проставляет isPreorder=true в фильтры.
   */
  static async getListPaginated(params?: {
    idPattern?: string;
    product?: { id: number };
    seller?: { id: number };
    responsible?: { id: number };
    createdPeriod?: { dateFrom: unknown; dateTo: unknown };
    preorderShipmentPeriod?: { dateFrom: unknown; dateTo: unknown };
    isOfferBased?: boolean;
    isFinished?: boolean;
    isDeleted?: boolean;
    requireNestedObjects?: boolean;
    requireOffers?: boolean;
    pagination?: RpcDataPagination;
    sort?: RpcDataSort;
  }): Promise<{ data: PreorderPosition[]; pagination: RpcDataPagination }> {
    const {pagination, sort, product, ...restFilters} = params ?? {};

    // Принудительно помечаем запрос как преордерный, иначе бек вернёт
    // обычные позиции заказов
    const filtersWithFlag: Record<string, unknown> = {...restFilters, isPreorder: true};

    if (product !== undefined) {
      // DataGridLoader передаёт объект целиком, бэку нужен только id
      filtersWithFlag.product = {id: product.id};
    }

    const response = await OrdersDAO.getPositionsList(
      filtersWithFlag,
      pagination,
      sort,
    );

    return {
      data: Core.utils.hydrateArray(response.data, PreorderPosition.fromServer),
      pagination: response.meta?.pagination,
    };
  }

  /**
   * Загрузить список позиций предзаказов.
   * Для использования вне реестра (диалоги, автокомплиты).
   */
  static async getList(
    params?: Parameters<typeof PreordersService.getListPaginated>[0]
  ): Promise<PreorderPosition[]> {
    return (await this.getListPaginated(params)).data;
  }

  /**
   * Загрузить одну позицию предзаказа по ID.
   * Возвращает null если позиция не найдена.
   */
  static async get(id: number): Promise<PreorderPosition | null> {
    const response = await OrdersDAO.get(id);

    let result: PreorderPosition | null = null;

    if (Core.utils.isObject(response.data)) {
      result = PreorderPosition.fromServer(response.data);
    }

    return result;
  }

  /**
   * Состояния позиции предзаказа — источник правды для колонки «Статус»
   * и вкладок воронки. id совпадают с ключами PreorderPosition.StatusNames.
   */
  static States = [
    {id: 'in-work', name: 'В работе', isFinished: false},
    {id: 'delivered', name: 'Доставлен', isFinished: true},
    {id: 'cancelled', name: 'Отменён', isDeleted: true},
  ] as const;

  /**
   * Маппинг id состояния → бэкенд-фильтры.
   * Для неизвестного id возвращает пустой объект — бек отдаст всё подряд.
   */
  static getStatusFilter(statusId: string): { isFinished?: boolean; isDeleted?: boolean } {
    const state = this.States.find(descriptor => descriptor.id === statusId);

    if (state === undefined) {
      return {};
    }

    const {id, name, ...filter} = state;

    return filter;
  }

  // ============================================================
  // Контекстное меню — позиция предзаказа
  // ============================================================

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (position: PreorderPosition) => { return OrdersService.showOrderPositionCard(position); },
        null,
        false,
        true,
        true,
      ),
      // Закоментил, тк при нажатии должен открыться попап, а для него нужен target, который неоткуда взять
      // По идее надо както event передавать, но пока проще скрыть
      // + в реестре уже есть кнопка создания заказа, дублировать как будто нет смысла
      // new ContextMenuItem(
      //   'Создать заказ',
      //   'add_shopping_cart',
      //   (position: PreorderPosition) => { return this.showBuyOneClickPopup(position); },
      //   (position: PreorderPosition) => position.getReasonCannotEdit(),
      //   /** Заказ по предзаказу оформляет покупатель */
      //   Core.currentPartner.isSeller === true,
      //   true,
      //   false,
      //   'addOrder'
      // ),
      new ContextMenuItem(
        'Карточка партнера',
        'person',
        (position: PreorderPosition) => { PartnersService.showPartnerCard(position.seller.id); },
        null,
        /** Карточка продавца — поставщик и так смотрит на себя */
        Core.currentPartner.isSeller === true,
        false,
      ),
      new ContextMenuItem(
        'Уведомить поставщика',
        'notifications_active',
        (position: PreorderPosition) => { return OrdersService.openNotifyPersonsDialog(position); },
        (position: PreorderPosition) => position.getReasonCannotNotify(),
        /** Уведомляет поставщика покупатель — самому себе не пишут */
        Core.currentPartner.isSeller === true,
      ),
      new ContextMenuItem(
        'Перенос ЖДД',
        'event',
        (position: PreorderPosition) => { return OrdersService.editDates(position); },
        (position: PreorderPosition) => position.getReasonCannotEditDates(),
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (position: PreorderPosition) => { OrdersService.showFeedDialog(position.id); },
        null,
        false,  // hidden
        false,  // refreshAfter — список не меняется
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (position: PreorderPosition) => { return OrdersService.deletePositionWithConfirm(position); },
        (position: PreorderPosition) => position.getReasonCannotDelete(),
      ),
    ];
  }
}

export default PreordersService;
